import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Badge, Button, Alert, Spinner, Form, Modal, Nav } from 'react-bootstrap';
import apiClient from '../../../utils/axios';
import { useAuth } from '../../auth/AuthContext';
import { parseISO, differenceInDays } from 'date-fns';
import './ProfilePage.css';
import { STATIC_URL as API_BASE_URL } from '../../../config';
import { fcmTokenManager } from '../../../utils/fcm';
import { GROUP_CODES, groupName as resolveGroupName, groupOrder } from '../../../config/groups';

const AuthenticIdField = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="d-flex align-items-center gap-2">
      <span className="text-truncate font-monospace" style={{ maxWidth: '200px' }} title={value}>
        {value.slice(0, 8)}...{value.slice(-6)}
      </span>
      <Button variant="link" size="sm" className="p-0 text-muted" onClick={handleCopy} title="複製 Authentic ID">
        <i className={`bi ${copied ? 'bi-check-lg text-success' : 'bi-copy'}`}></i>
      </Button>
    </div>
  );
};

const ProfilePage = () => {
  const { user, loading, error, fetchUserInfo } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [expiryWarning, setExpiryWarning] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [memberAvatars, setMemberAvatars] = useState({});
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [permissionsError, setPermissionsError] = useState(null);
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [nickname, setNickname] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  const [activeGroup, setActiveGroup] = useState(GROUP_CODES[0]);
  const [permissionStatus, setPermissionStatus] = useState(
    'Notification' in window ? Notification.permission : 'unsupported'
  );
  const [togglingIds, setTogglingIds] = useState(new Set());
  const [notifyError, setNotifyError] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(
    !!localStorage.getItem('fcm_token') && !fcmTokenManager.isDisabledByUser()
  );
  const [pushToggling, setPushToggling] = useState(false);

  const isNearExpiry = (date) => {
    if (!date || date === 'permanent') return false;
    
    try {
      const expiryDate = parseISO(date);
      const today = new Date();
      
      // 如果已過期，則返回 false（因為這種情況會另外顯示為已過期）
      if (expiryDate < today) return false;
      
      // 計算到期日與今天的天數差
      const daysRemaining = differenceInDays(expiryDate, today);
      
      // 如果剩餘天數小於等於 3，則視為快要到期
      return daysRemaining <= 3;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    document.title = '個人資料';
    
    if (user && user.expireDate && user.expireDate !== 'permanent' && !user.isExpired) {
      try {
        const expiryDate = parseISO(user.expireDate);
        const today = new Date();
        const daysLeft = differenceInDays(expiryDate, today);
        
        // 只有當剩餘天數小於或等於 3 天時，才顯示提醒
        if (daysLeft <= 3) {
          setExpiryWarning({
            days: daysLeft,
            expiryDate: user.formattedExpiryDate
          });
        } else {
          setExpiryWarning(null);
        }
      } catch (error) {
        setExpiryWarning(null);
      }
    } else {
      setExpiryWarning(null);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.id) {
      fetchUserPermissions();
    }
  }, [user]);

  // 依權限分組並排序成員，快取避免每次 render 重算
  const groupedPermissions = useMemo(() => {
    if (!permissions || !permissions.permissions || !Array.isArray(permissions.permissions)) {
      return {};
    }

    // Sort all members first by group, term, kana, and name
    const sortedPermissions = [...permissions.permissions].sort((a, b) => {
      // Primary sort: group (依 HOME_GROUPS 順序)
      const groupA = groupOrder(a.group);
      const groupB = groupOrder(b.group);
      if (groupA !== groupB) {
        return groupA - groupB;
      }
      // Secondary sort: term (ascending - 小到大)
      const termA = a.term || 0;
      const termB = b.term || 0;
      if (termA !== termB) {
        return termA - termB;
      }
      // Tertiary sort: by kana (50音順)
      if (a.kana && b.kana) {
        const kanaCompare = a.kana.localeCompare(b.kana, 'ja-JP');
        if (kanaCompare !== 0) {
          return kanaCompare;
        }
      }
      // Quaternary sort: by name (if kana is same or missing)
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB, 'ja-JP');
    });

    // Group the sorted members - only use essential fields
    return sortedPermissions.reduce((groups, member) => {
      const group = member.group || '未分類';
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push({
        id: member.id,
        name: member.name,
        group: member.group,
        avatar: member.avatar || memberAvatars[member.id],
        is_notify_enabled: member.is_notify_enabled,
      });
      return groups;
    }, {});
  }, [permissions, memberAvatars]);

  // 若目前選中的團沒有任何權限，切換到第一個有權限的團
  useEffect(() => {
    const availableGroups = GROUP_CODES.filter((g) => (groupedPermissions[g]?.length || 0) > 0);
    if (availableGroups.length > 0 && !availableGroups.includes(activeGroup)) {
      setActiveGroup(availableGroups[0]);
    }
  }, [groupedPermissions, activeGroup]);

  const fetchUserPermissions = async () => {
    setLoadingPermissions(true);
    setPermissionsError(null);

    try {
      // 併行抓取權限與成員清單，成員清單用來取得後端提供的 avatar 路徑
      const [response, membersResponse] = await Promise.all([
        apiClient.get('/users/me/permissions'),
        apiClient.get('/members').catch(() => null),
      ]);

      if (membersResponse && Array.isArray(membersResponse.data)) {
        const avatarMap = membersResponse.data.reduce((acc, m) => {
          if (m && m.id != null && m.avatar) {
            acc[m.id] = m.avatar;
          }
          return acc;
        }, {});
        setMemberAvatars(avatarMap);
      }

      // 更寬鬆的檢查：只要有 permissions 陣列就接受
      if (response.data && Array.isArray(response.data.permissions)) {
        setPermissions(response.data);
      } else if (response.data) {
        // 可能整個 response.data 就是陣列
        setPermissions({ permissions: Array.isArray(response.data) ? response.data : [] });
      } else {
        setPermissions({ permissions: [] });
      }
    } catch (error) {
      // 不要顯示錯誤訊息,而是顯示空權限列表
      setPermissions({ permissions: [] });
      setPermissionsError(null); // 不顯示錯誤
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handleNotifyToggle = async (memberId, currentEnabled) => {
    setTogglingIds((ids) => new Set(ids).add(memberId));
    setNotifyError(null);
    try {
      const res = await apiClient.post(`/members/${memberId}/notification`, { enabled: !currentEnabled });
      if (res.status === 204) {
        setPermissions((prev) => ({
          ...prev,
          permissions: prev.permissions.map((m) =>
            m.id === memberId ? { ...m, is_notify_enabled: !currentEnabled } : m
          ),
        }));
      }
    } catch {
      setNotifyError('更新通知設定失敗，請稍後再試');
    } finally {
      setTogglingIds((ids) => {
        const next = new Set(ids);
        next.delete(memberId);
        return next;
      });
    }
  };

  const handleRequestPermission = async () => {
    const result = await Notification.requestPermission();
    setPermissionStatus(result);
    if (result === 'granted') {
      fcmTokenManager.clearToken();
      const ok = await fcmTokenManager.initializePush(undefined, { force: true });
      if (ok) setPushEnabled(true);
    }
  };

  const handlePushToggle = async () => {
    setPushToggling(true);
    setNotifyError(null);
    try {
      if (pushEnabled) {
        await fcmTokenManager.unregister();
        setPushEnabled(false);
      } else {
        const ok = await fcmTokenManager.initializePush(undefined, { force: true });
        if (ok) setPushEnabled(true);
      }
    } catch {
      setNotifyError('推播通知設定失敗，請稍後再試');
    } finally {
      setPushToggling(false);
    }
  };

  useEffect(() => {
    if (user && user.nickname) {
      setNickname(user.nickname);
    }
  }, [user]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchUserInfo();
      setAlertMessage({ type: 'success', message: '資料已成功更新！' });
      setTimeout(() => setAlertMessage(null), 3000);
    } catch (err) {
      setAlertMessage({ type: 'danger', message: '更新資料失敗，請稍後再試。' });
    } finally {
      setRefreshing(false);
    }
  };

  const openEditModal = () => {
    setNickname(user.nickname || '');
    setEditError('');
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
  };

  const handleSubmitNickname = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError('');

    try {
      await apiClient.patch('/users/me', { nickname });
      setShowEditModal(false);
      await fetchUserInfo();
      setAlertMessage({ type: 'success', message: '暱稱已成功更新！' });
      setTimeout(() => setAlertMessage(null), 3000);
    } catch (err) {
      if (err.response?.status === 400) {
        setEditError('請提供有效的暱稱');
      } else if (err.response?.status === 404) {
        setEditError('用戶不存在');
      } else {
        setEditError('更新暱稱失敗，請稍後再試');
      }
    } finally {
      setEditLoading(false);
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return '系統管理員';
      case 'member':
        return '會員用戶';
      case 'user':
        return '一般用戶';
      case 'access':
        return '瀏覽權限用戶';
      default:
        return role || '未定義';
    }
  };

  const getSubscriptionStatus = () => {
    if (!user) return '未登入';
    
    if (!user.isActive) {
      return <Badge bg="warning">帳號未啟用</Badge>;
    }
    
    if (user.isExpired) {
      return <Badge bg="danger">已過期</Badge>;
    }
    
    if (user.expireDate === 'permanent' || user.formattedExpiryDate === '永久') {
      return <Badge bg="success">永久有效</Badge>;
    }
    
    if (user.expireDate) {
      return <Badge bg="success">有效期至 {user.formattedExpiryDate}</Badge>;
    }
    
    return <Badge bg="success">永久有效</Badge>;
  };

  if (loading) {
    return (
      <Container className="my-5 pt-5 text-center">
        <Spinner animation="border" role="status" variant="primary" />
        <p className="mt-3">載入個人資料中...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5 pt-5">
        <Alert variant="danger">
          載入個人資料時發生錯誤: {error}
        </Alert>
        <div className="text-center mt-3">
          <Button variant="primary" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? '重試中...' : '重試'}
          </Button>
        </div>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container className="my-5 pt-5">
        <Alert variant="warning">
          您尚未登入，請先登入以查看個人資料。
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="profile-container">
      {alertMessage && (
        <Alert variant={alertMessage.type} onClose={() => setAlertMessage(null)} dismissible>
          {alertMessage.message}
        </Alert>
      )}
      
      {expiryWarning && (
        <Alert variant="warning" className="mb-4 d-flex align-items-center">
          <i className="bi bi-exclamation-triangle-fill me-2 fs-5"></i>
          <div>
            <strong>訂閱即將到期！</strong> 您的會員資格將在 <Badge bg="danger">{expiryWarning.days} 天後</Badge> 到期 (到期日: {expiryWarning.expiryDate})。
            請聯繫管理員續訂您的會員資格。
          </div>
        </Alert>
      )}
      
      <Row className="mb-4 justify-content-between align-items-center">
        <Col>
          <h2 className="profile-title">個人資料</h2>
          <p className="text-muted">查看您的個人資料和帳戶設定</p>
        </Col>
        <Col xs="auto" className="d-flex align-items-center">
          <Button 
            variant="outline-primary" 
            onClick={handleRefresh} 
            disabled={refreshing}
            className="d-flex align-items-center"
          >
            {refreshing ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                更新中...
              </>
            ) : (
              <>
                <i className="bi bi-arrow-clockwise me-2"></i>
                重新整理
              </>
            )}
          </Button>
        </Col>
      </Row>

      <div className="profile-content">
        <Row className="mb-5">
          <Col lg={3} md={4} className="mb-4">
            <div className="profile-sidebar p-4 bg-light rounded shadow-sm">
              <div className="text-center mb-4">
                <div className="profile-avatar-container mb-3 mx-auto">
                  <img 
                    src={user.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.username)}&background=random`} 
                    alt="個人頭像"
                    className="profile-avatar rounded-circle" 
                  />
                </div>
                <h4 className="mb-1">{user.username || '未設置用戶名'}</h4>
                
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <p className="text-muted mb-0 me-2">{user.nickname || '未設置暱稱'}</p>
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="p-0 edit-nickname-btn" 
                    onClick={openEditModal}
                  >
                    <i className="bi bi-pencil-fill"></i>
                  </Button>
                </div>
                
                <p className="text-muted">{user.email || '未設置郵箱'}</p>
                <Badge bg={user.isAdmin ? 'danger' : 'primary'} className="mb-2">
                  {user.isAdmin ? '管理員' : getRoleLabel(user.role)}
                </Badge>
                <div className="mt-2">
                  {getSubscriptionStatus()}
                </div>
              </div>
            </div>
          </Col>

          <Col lg={9} md={8}>
            <div className="profile-details bg-white rounded p-4 shadow-sm">
              <h4 className="border-bottom pb-3 mb-4">詳細資料</h4>
              
              <Row className="mb-4">
                <Col xs={12} md={4} className="fw-bold mb-2 mb-md-0">用戶名稱</Col>
                <Col xs={12} md={8}>{user.username}</Col>
              </Row>
              
              <Row className="mb-4">
                <Col xs={12} md={4} className="fw-bold mb-2 mb-md-0">暱稱</Col>
                <Col xs={12} md={8}>
                  <div className="d-flex align-items-center">
                    <span>{user.nickname || '未設置'}</span>
                    <Button 
                      variant="link" 
                      size="sm" 
                      className="p-0 ms-2 edit-nickname-btn" 
                      onClick={openEditModal}
                    >
                      <i className="bi bi-pencil-fill"></i>
                    </Button>
                  </div>
                </Col>
              </Row>
              
              <Row className="mb-4">
                <Col xs={12} md={4} className="fw-bold mb-2 mb-md-0">電子郵件</Col>
                <Col xs={12} md={8}>{user.email}</Col>
              </Row>
              
              <Row className="mb-4">
                <Col xs={12} md={4} className="fw-bold mb-2 mb-md-0">Authentic ID</Col>
                <Col xs={12} md={8}>
                  {user.authenticId ? (
                    <AuthenticIdField value={user.authenticId} />
                  ) : '未綁定'}
                </Col>
              </Row>
              
              <Row className="mb-4">
                <Col xs={12} md={4} className="fw-bold mb-2 mb-md-0">註冊日期</Col>
                <Col xs={12} md={8}>
                  {user.registrationDate ? 
                    new Date(user.registrationDate).toLocaleDateString('zh-TW') : 
                    '未知'}
                </Col>
              </Row>
              
              <Row className="mb-4">
                <Col xs={12} md={4} className="fw-bold mb-2 mb-md-0">帳號狀態</Col>
                <Col xs={12} md={8}>
                  {user.isActive ? 
                    <Badge bg="success">已啟用</Badge> : 
                    <Badge bg="warning">待審核</Badge>}
                </Col>
              </Row>
              
              <Row className="mb-4">
                <Col xs={12} md={4} className="fw-bold mb-2 mb-md-0">權限等級</Col>
                <Col xs={12} md={8}>{getRoleLabel(user.role)}</Col>
              </Row>
              
              <Row className="mb-4">
                <Col xs={12} md={4} className="fw-bold mb-2 mb-md-0">訂閱狀態</Col>
                <Col xs={12} md={8}>
                  {user.isExpired ? (
                    <span className="text-danger">已過期 ({user.formattedExpiryDate})</span>
                  ) : isNearExpiry(user.expireDate) ? (
                    <span className="text-warning">快到期 ({user.formattedExpiryDate})</span>
                  ) : user.expireDate === 'permanent' || user.formattedExpiryDate === '永久' ? (
                    <span className="text-success">永久有效</span>
                  ) : user.expireDate ? (
                    <span className="text-success">有效期至 {user.formattedExpiryDate}</span>
                  ) : (
                    <span className="text-success">永久有效</span>
                  )}
                </Col>
              </Row>
              
              {user.additionalInfo && (
                <Row className="mb-4">
                  <Col xs={12} md={4} className="fw-bold mb-2 mb-md-0">額外資訊</Col>
                  <Col xs={12} md={8}>{user.additionalInfo}</Col>
                </Row>
              )}
            </div>
            
            <div className="profile-details bg-white rounded p-4 shadow-sm mt-4">
              <div className="d-flex align-items-center justify-content-between border-bottom pb-3 mb-3 flex-wrap gap-2">
                <h4 className="mb-0">瀏覽權限</h4>
                {permissionStatus === 'unsupported' ? null : (
                  <div className="d-flex align-items-center gap-2">
                    <small className="text-muted">推播通知：</small>
                    {permissionStatus === 'granted' && (
                      pushToggling
                        ? <Spinner animation="border" size="sm" />
                        : <Form.Check
                            type="switch"
                            id="push-enabled-switch"
                            checked={pushEnabled}
                            onChange={handlePushToggle}
                            label={pushEnabled ? '已開啟' : '已關閉'}
                          />
                    )}
                    {permissionStatus === 'denied' && <Badge bg="danger">已拒絕</Badge>}
                    {permissionStatus === 'default' && (
                      <Button variant="outline-primary" size="sm" onClick={handleRequestPermission}>
                        允許推播通知
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {notifyError && (
                <Alert variant="danger" dismissible onClose={() => setNotifyError(null)} className="mb-3">
                  {notifyError}
                </Alert>
              )}

              {loadingPermissions ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">載入權限資料中...</p>
                </div>
              ) : !permissions || !permissions.permissions || permissions.permissions.length === 0 ? (
                <Alert variant="info">您目前沒有任何特定成員的瀏覽權限。</Alert>
              ) : (
                <>
                  <Nav variant="tabs" className="mb-3" activeKey={activeGroup} onSelect={setActiveGroup}>
                    {GROUP_CODES.map((group) => {
                      const count = groupedPermissions[group]?.length || 0;
                      // 完全沒有該團權限時不顯示標籤
                      if (count === 0) return null;
                      const label = resolveGroupName(group);
                      return (
                        <Nav.Item key={group}>
                          <Nav.Link eventKey={group}>
                            {label}
                            <Badge bg="primary" className="ms-1">{count}</Badge>
                          </Nav.Link>
                        </Nav.Item>
                      );
                    })}
                  </Nav>
                  <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
                    {(groupedPermissions[activeGroup] || []).map(member => (
                      <div key={member.id} className="col">
                        <div className="d-flex align-items-center border rounded p-2 gap-2">
                          <img
                            src={member.avatar
                              ? `${API_BASE_URL}${member.avatar}`
                              : `${API_BASE_URL}/images/avatar/${member.group}.png`}
                            alt={member.name}
                            className="rounded-circle flex-shrink-0"
                            width="40"
                            height="40"
                            onError={(e) => {
                              if (!e.target.dataset.fallbackAttempted) {
                                e.target.dataset.fallbackAttempted = 'true';
                                e.target.src = `${API_BASE_URL}/images/avatar/${member.group}.png`;
                              }
                            }}
                          />
                          <div className="d-flex flex-column flex-grow-1 ms-1 overflow-hidden">
                            <span className="fw-medium text-truncate">{member.name}</span>
                            <a
                              href={`/members/${member.id}`}
                              className="text-decoration-none small"
                            >
                              查看訊息
                            </a>
                          </div>
                          {togglingIds.has(member.id) ? (
                            <Spinner animation="border" size="sm" className="flex-shrink-0" />
                          ) : (
                            <Form.Check
                              type="switch"
                              id={`notify-${member.id}`}
                              className="flex-shrink-0"
                              checked={member.is_notify_enabled ?? false}
                              onChange={() => handleNotifyToggle(member.id, member.is_notify_enabled)}
                              disabled={permissionStatus !== 'granted'}
                              title={permissionStatus !== 'granted' ? '請先允許推播通知權限' : '推播通知'}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </Col>
        </Row>
      </div>

      <Modal show={showEditModal} onHide={closeEditModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>修改暱稱</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editError && (
            <Alert variant="danger" className="mb-3">
              {editError}
            </Alert>
          )}
          
          <Form onSubmit={handleSubmitNickname}>
            <Form.Group className="mb-3">
              <Form.Label>暱稱</Form.Label>
              <Form.Control
                type="text"
                placeholder="請輸入新的暱稱"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                autoFocus
              />
              <Form.Text className="text-muted">
                暱稱將顯示在您的個人資料頁面上。
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEditModal} disabled={editLoading}>
            取消
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmitNickname} 
            disabled={editLoading}
          >
            {editLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                儲存中...
              </>
            ) : '儲存變更'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProfilePage;